<template>
  <div class="home-agent-page" :style="{ height: isDashboard ? '100vh' : 'calc(100vh - 48px)', overflow: isDashboard ? 'auto' : 'hidden' }">
    <div class="page-row" :style="{ gap: drawerOpen ? '20px' : '0' }">
      <div class="page-content">
        <div class="agent-glow"></div>
        <div class="page-inner">
          <div class="agent-header">
            <h1 class="header-title">{{ title }}</h1>
            <p class="header-desc">{{ description }}</p>
          </div>

          <div class="input-area">
            <div class="input-shell">
              <textarea v-model="question" class="input-textarea" :placeholder="isDisabled ? '正在连接...' : placeholder" :disabled="isDisabled" ref="inputTextarea" @keydown="handleKeyDown"></textarea>
              <div class="input-toolbar">
                <div class="toolbar-right">
                  <div class="send-btn" :class="{ disabled: isDisabled }" @click="handleSend">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="currentColor" style="transform: rotate(-45deg)">
                      <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"></path>
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 快捷问题骨架屏 -->
          <div v-if="quickQuestionsLoading" class="quick-questions">
            <div class="question-skeleton" v-for="n in 4" :key="'skel-' + n">
              <span class="skel-icon"></span>
              <span class="skel-text"></span>
            </div>
          </div>

          <!-- 快捷问题 -->
          <div v-else-if="quickQuestions.length" class="quick-questions">
            <div class="question-item" :class="{ disabled: isDisabled }" v-for="(item, index) in quickQuestions" :key="index" @click="!isDisabled && handleSend(item.text)">
              <span class="q-icon" :style="{ background: item.iconBg, color: item.iconColor }">
                <i :class="item.icon"></i>
              </span>
              <span class="q-text">{{ item.text }}</span>
            </div>
          </div>

          <div style="color: #999; font-size: 12px; padding: 34px 0"><i class="el-icon-s-data"></i> 数据概览</div>

          <div class="slot-wrap">
            <slot></slot>
          </div>
        </div>
      </div>
      <div class="drawer-panel" :style="{ width: drawerOpen ? actualWidth + 'px' : '0' }">
        <div class="drawer-inner" :style="{ width: actualWidth + 'px' }">
          <slot name="drawer-panel"></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const AGENT_TYPE_TO_NAME = {
  governance: 'ontology_question',
  indicator: 'indicator_question',
  decision: 'decision_question'
}

const QUESTION_STYLES = {
  governance: [
    { icon: 'el-icon-s-operation', iconBg: '#EEF2FF', iconColor: '#4A5FF3' },
    { icon: 'el-icon-discover', iconBg: '#E8F5E9', iconColor: '#4CAF50' },
    { icon: 'el-icon-s-data', iconBg: '#E3F2FD', iconColor: '#2196F3' },
    { icon: 'el-icon-share', iconBg: '#E8F0FE', iconColor: '#1976D2' }
  ],
  indicator: [
    { icon: 'el-icon-menu', iconBg: '#EEF2FF', iconColor: '#4A5FF3' },
    { icon: 'el-icon-suitcase', iconBg: '#E8F5E9', iconColor: '#4CAF50' },
    { icon: 'el-icon-s-data', iconBg: '#E3F2FD', iconColor: '#2196F3' },
    { icon: 'el-icon-document', iconBg: '#FFF3E0', iconColor: '#FF9800' }
  ],
  decision: [
    { icon: 'el-icon-s-data', iconBg: '#EEF2FF', iconColor: '#4A5FF3' },
    { icon: 'el-icon-s-marketing', iconBg: '#FFF3E0', iconColor: '#FF9800' },
    { icon: 'el-icon-search', iconBg: '#E8F5E9', iconColor: '#4CAF50' },
    { icon: 'el-icon-s-opportunity', iconBg: '#E3F2FD', iconColor: '#2196F3' }
  ]
}

export default {
  name: 'AgentLayout',
  props: {
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    drawerOpen: { type: Boolean, default: false },
    drawerWidth: { type: Number, default: 800 },
    agentType: { type: String, default: '' }
  },
  data() {
    return {
      question: '',
      actualWidth: 800,
      quickQuestions: [],
      quickQuestionsLoading: false
    }
  },
  computed: {
    isDashboard() {
      return this.$route && this.$route.path.includes('dashboard')
    },
    isHandshaked() {
      return this.$store.state.cosmoClaw.isHandshaked
    },
    isDisabled() {
      return !this.isHandshaked
    }
  },
  methods: {
    handleKeyDown(e) {
      if (e.key !== 'Enter') return
      // Shift / Alt / Ctrl / Meta + Enter：在光标处插入换行
      if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) {
        e.preventDefault()
        this.insertNewline()
        return
      }
      e.preventDefault()
      this.handleSend()
    },
    insertNewline() {
      const ta = this.$refs.inputTextarea
      if (!ta) return
      const start = ta.selectionStart
      const end = ta.selectionEnd
      const value = ta.value || ''
      this.question = value.slice(0, start) + '\n' + value.slice(end)
      this.$nextTick(() => {
        if (this.$refs.inputTextarea) {
          this.$refs.inputTextarea.selectionStart = start + 1
          this.$refs.inputTextarea.selectionEnd = start + 1
        }
      })
    },
    handleSend(text) {
      if (this.isDisabled) return
      const content = (typeof text === 'string' ? text : this.question).trim()
      if (!content) return
      this.question = ''
      const currentAgentName = this.$store.state.cosmoClaw.currentAgentName || 'main'
      this.$store.dispatch('cosmoClaw/createNewSession', { agentName: currentAgentName, forceNew: true }).then((sessionKey) => {
        setTimeout(() => {
          this.$store.dispatch('cosmoClaw/sendMessage', { content, sessionKey })
        }, 500)
      })
    },
    updateDrawerWidth() {
      this.actualWidth = window.innerWidth < 1400 ? 600 : this.drawerWidth
    },
    async fetchQuickQuestions() {
      const name = AGENT_TYPE_TO_NAME[this.agentType]
      if (!name) {
        this.quickQuestions = []
        return
      }
      this.quickQuestionsLoading = true
      const styles = QUESTION_STYLES[this.agentType] || []
      try {
        const res = await fetch(`/tianshu-api/bigdata/ind-base-data/remark?name=${name}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        })
        const data = await res.json()
        if (data && data.code === 0 && data.data && data.data.remark) {
          const texts = JSON.parse(data.data.remark)
          this.quickQuestions = texts.map((text, index) => ({
            text,
            ...(styles[index] || { icon: 'el-icon-s-data', iconBg: '#EEF2FF', iconColor: '#4A5FF3' })
          }))
        }
      } catch {
        this.quickQuestions = styles.map(s => ({ text: '', ...s }))
      } finally {
        this.quickQuestionsLoading = false
      }
    }
  },
  watch: {
    agentType() {
      this.fetchQuickQuestions()
    }
  },
  mounted() {
    this.updateDrawerWidth()
    window.addEventListener('resize', this.updateDrawerWidth)
    this.fetchQuickQuestions()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateDrawerWidth)
  }
}
</script>

<style lang="scss" scoped>
.home-agent-page {
  height: calc(100vh - 48px);
  overflow: hidden;
  // padding-top: 30px;
}

.page-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.page-content {
  position: relative;
  flex: 1;
  min-width: 0;
  margin: 0 auto;
  padding: 24px 32px;
  padding-top: 120px;
  background: radial-gradient(circle at top left, rgba(255, 255, 255, 0.96) 0%, rgba(236, 243, 255, 0.94) 38%, rgba(222, 236, 255, 0.9) 68%, rgba(212, 229, 255, 0.84) 100%),
    linear-gradient(135deg, rgba(255, 255, 255, 0.96) 0%, rgba(229, 239, 255, 0.92) 52%, rgba(210, 228, 255, 0.88) 100%);
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;

  &::-webkit-scrollbar {
    width: 5px;
    background: transparent;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: transparent;
    border-radius: 3px;
  }

  &:hover {
    scrollbar-color: rgba(148, 163, 184, 0.5) transparent;
    &::-webkit-scrollbar-thumb {
      background: rgba(148, 163, 184, 0.5);
    }
  }

  .agent-glow {
    position: absolute;
    left: 50%;
    top: 14%;
    height: 280px;
    width: 560px;
    transform: translateX(-50%);
    border-radius: 50%;
    background: radial-gradient(circle, rgba(91, 111, 248, 0.16) 0%, rgba(91, 111, 248, 0.06) 38%, rgba(255, 255, 255, 0) 72%);
    filter: blur(12px);
    pointer-events: none;
  }

  .page-inner {
    width: 100%;
    max-width: 1100px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.agent-header {
  text-align: center;
  margin-bottom: 32px;
  max-width: 800px;

  .header-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a1a2e;
    margin: 0 0 12px 0;
  }

  .header-desc {
    font-size: 14px;
    color: #666;
    line-height: 1.8;
    margin: 0;
  }
}

.input-area {
  width: 100%;
  max-width: 1100px;
  margin-bottom: 24px;

  .input-shell {
    background: #fff;
    border: 1px solid rgb(203 213 225 / 0.8);
    border-radius: 28px;
    padding: 20px 24px 16px;
    display: flex;
    flex-direction: column;

    .input-textarea {
      min-height: 78px;
      width: 100%;
      resize: none;
      border: none;
      background: transparent;
      font-size: 15px;
      line-height: 1.8;
      color: #334155;
      font-family: inherit;
      outline: none;

      &::placeholder {
        color: #cbd5e1;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
    }

    .input-toolbar {
      margin-top: 12px;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      border-top: 1px solid #f1f5f9;
      padding-top: 12px;

      .toolbar-right {
        display: flex;
        align-items: center;
      }

      .send-btn {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        background: #eef2ff;
        color: #5b6ff8;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        flex-shrink: 0;
        transition: all 0.2s;

        &:hover {
          background: #dee6ff;
        }

        &.disabled {
          opacity: 0.4;
          cursor: not-allowed;
          &:hover {
            background: #eef2ff;
          }
        }
      }
    }
  }
}

/* ── 快捷问题 ── */
.quick-questions {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;

  .question-item {
    background: #fff;
    border-radius: 12px;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    cursor: pointer;
    transition: all 0.3s;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .q-icon {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      i {
        font-size: 18px;
      }
    }

    .q-text {
      font-size: 13px;
      color: #333;
      line-height: 1.4;
    }

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
    }
  }

  .question-skeleton {
    background: #fff;
    border-radius: 12px;
    padding: 10px 20px;
    display: flex;
    align-items: center;
    gap: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

    .skel-icon {
      width: 28px;
      height: 28px;
      border-radius: 8px;
      flex-shrink: 0;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skel-shimmer 1.5s infinite;
    }

    .skel-text {
      flex: 1;
      height: 16px;
      border-radius: 4px;
      background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
      background-size: 200% 100%;
      animation: skel-shimmer 1.5s infinite;
    }
  }
}

@keyframes skel-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.slot-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.drawer-panel {
  flex-shrink: 0;
  align-self: stretch;
  overflow: hidden;
  transition: width 0.3s ease;
  background: #fff;
  border-radius: 28px 0 0 28px;
  box-shadow: -4px 0 20px rgba(0, 0, 0, 0.08);
}

.drawer-inner {
  width: 800px;
  height: 100%;
  overflow-y: auto;
}
</style>
